/**
 * Types for the Game Library (Bibliothèque de parties)
 */

import type { CardNumber, Contract, GameScore } from './game';

// ============================================
// Predefined tags
// ============================================

export const PREDEFINED_TAGS = [
  'à classer',
  'souci IA',
  'défense',
  'attaque',
  'petit au bout',
  'chelem',
  'écart',
  'contrat',
] as const;

export type PredefinedTag = typeof PREDEFINED_TAGS[number];

/** Auto-generated tags (not manually assignable) */
export const AUTO_TAGS = ['partagée', 'commentée', 'sondage'] as const;
export type AutoTag = typeof AUTO_TAGS[number];

// ============================================
// Game data stored in JSONB
// ============================================

export interface LibraryPlayerInfo {
  position: number;
  displayName?: string;
  isHuman: boolean;
  isBot: boolean;
  botVersion?: string;
}

export interface LibraryBidRecord {
  player: number;
  bid: Contract;
  timestamp: number;
}

export interface LibraryCardPlay {
  player: number;
  card: CardNumber;
  timestamp: number;
}

export interface LibraryTrickHistory {
  trickNumber: number;
  leader: number;
  cards: LibraryCardPlay[];
  winner: number;
}

export interface LibraryGameData {
  players: LibraryPlayerInfo[];
  playerCount: number;
  dealer: number;
  taker: number;
  partner?: number;
  contract: Contract;
  calledKing?: CardNumber;
  initialHands: Record<number, CardNumber[]>;
  chien: CardNumber[];
  ecart: CardNumber[];
  bids: LibraryBidRecord[];
  tricks: LibraryTrickHistory[];
  poignees: Record<number, CardNumber[]>;
  score?: GameScore;
}

// ============================================
// Origin types
// ============================================

export type LibraryOriginType =
  | 'libre'
  | 'differe_indiv'
  | 'differe_tq'
  | 'defi'
  | 'training';

/** Métadonnées d'origine enrichies (différé, etc.) */
export interface LibraryDiffereOriginMetadata {
  differeType: 'indiv' | 'tripl' | 'quad';
  differeId: number;
  subType?: string;
  dateStart?: string;
  dateEnd?: string;
  playerPseudo: string;
  playerDisplay?: string;
  dealNum?: number;
  partieId?: number;
}

export type LibraryOriginMetadata = LibraryDiffereOriginMetadata;

// ============================================
// Library game (as returned by API)
// ============================================

export interface LibraryGame {
  id: number;
  owner_pseudo: string;
  game_data: LibraryGameData;
  origin_type: LibraryOriginType;
  origin_id?: string;
  origin_donne_num?: number;
  origin_metadata?: LibraryOriginMetadata;
  recommended_position: number;
  is_modified: boolean;
  is_partial: boolean;
  partial_phase?: number;
  partial_trick_num?: number;
  comments_enabled: boolean;
  note?: string;
  tags: string[];
  created_at: string;
  updated_at: string;
  /** True if this game comes from a différé still in progress */
  differe_pending?: boolean;
}

/** Compact summary for list display */
export interface LibraryGameSummary {
  id: number;
  owner_pseudo: string;
  origin_type: LibraryOriginType;
  origin_id?: string;
  origin_donne_num?: number;
  origin_metadata?: LibraryOriginMetadata;
  contract: Contract;
  taker_position: number;
  player_count: number;
  is_partial: boolean;
  is_modified: boolean;
  recommended_position: number;
  partial_phase?: number;
  partial_trick_num?: number;
  note?: string;
  tags: string[];
  score_won?: boolean;
  score_taker_points?: number;
  created_at: string;
  differe_pending?: boolean;
  author_comment?: string | null;
  comment_count?: number;
  last_comment?: string | null;
  last_comment_author?: string | null;
}

// ============================================
// Shared games
// ============================================

export type ShareType = 'public' | 'private';

export interface SharedGameRecipient {
  recipient_type: 'player' | 'team';
  recipient_id: string;
}

export interface LibrarySharedGame {
  id: number;
  source_game_id?: number;
  shared_by: string;
  game_data: LibraryGameData;
  origin_type: LibraryOriginType;
  origin_metadata?: LibraryOriginMetadata;
  recommended_position: number;
  is_partial: boolean;
  partial_phase?: number;
  partial_trick_num?: number;
  is_poll: boolean;
  share_type: ShareType;
  comments_enabled: boolean;
  shared_at: string;
  cancelled_at?: string;
  tags?: string[];
}

export interface LibrarySharedGameSummary {
  id: number;
  shared_by: string;
  origin_type: LibraryOriginType;
  origin_metadata?: LibraryOriginMetadata;
  contract: Contract;
  player_count: number;
  is_partial: boolean;
  is_poll: boolean;
  share_type: ShareType;
  recommended_position: number;
  partial_phase?: number;
  partial_trick_num?: number;
  score_won?: boolean;
  score_taker_points?: number;
  shared_at: string;
  tags?: string[];
  poll_response_count?: number;
  user_has_voted?: boolean;
  author_comment?: string | null;
  comment_count?: number;
  last_comment?: string | null;
  last_comment_author?: string | null;
  /** Partage non encore ouvert par l'utilisateur */
  is_new?: boolean;
  /** Le destinataire peut copier / transmettre le lien */
  allow_reshare?: boolean;
}

export interface LibraryShareInfo {
  id: number;
  source_game_id?: number;
  origin_type: LibraryOriginType;
  contract: Contract;
  player_count: number;
  is_partial: boolean;
  partial_phase?: number;
  partial_trick_num?: number;
  recommended_position?: number;
  is_poll: boolean;
  share_type: ShareType;
  shared_at: string;
  tags: string[];
  score_taker_points?: number;
  poll_response_count: number;
  recipients: Array<{ type: string; id: string }>;
  comment_count?: number;
  last_comment?: string | null;
  last_comment_author?: string | null;
  allow_reshare?: boolean;
}

// ============================================
// Comments
// ============================================

export type CommentTargetType = 'game' | 'trick' | 'card' | 'bid';

export interface LibraryComment {
  id: number;
  game_id: number;
  game_source: 'own' | 'shared';
  author: string;
  target_type: CommentTargetType;
  target_trick_num?: number;
  target_card_index?: number;
  text: string;
  created_at: string;
  updated_at: string;
}

// ============================================
// Polls
// ============================================

export type PollVoteType = 'card' | 'bid' | 'ecart';

export interface PollResponse {
  pseudo: string;
  card_played: CardNumber;
  vote_type: PollVoteType;
  vote_value?: string;
  responded_at: string;
}

export interface PollVotePayload {
  card_played: number;
  vote_type?: PollVoteType;
  vote_value?: string;
}

// ============================================
// API payloads
// ============================================

export interface LibraryOriginMatch {
  id: number;
  created_at: string;
  note?: string;
  tags: string[];
  origin_type: LibraryOriginType;
  origin_id?: string;
  origin_donne_num?: number;
  origin_metadata?: LibraryOriginMetadata;
  comment_count?: number;
  author_comment?: string | null;
}

export interface LibraryOriginLookupResponse {
  found: boolean;
  game: LibraryOriginMatch | null;
}

export interface AddToLibraryPayload {
  game_data: LibraryGameData;
  origin_type: LibraryOriginType;
  origin_id?: string;
  origin_donne_num?: number;
  origin_metadata?: LibraryOriginMetadata;
  recommended_position: number;
  is_modified?: boolean;
  is_partial?: boolean;
  partial_phase?: number;
  partial_trick_num?: number;
  tags?: string[];
  note?: string;
}

export interface ShareGamePayload {
  share_type: ShareType;
  recipients?: SharedGameRecipient[];
  is_poll?: boolean;
  comments_enabled?: boolean;
  allow_reshare?: boolean;
}

export interface UpdateLibraryGamePayload {
  tags?: string[];
  note?: string;
  recommended_position?: number;
  comments_enabled?: boolean;
}

// ============================================
// Load in training module
// ============================================

export type LibraryLoadMode = 'replay' | 'play';
export type BiddingMode = 'auto' | 'free';

export interface LoadLibraryGameOptions {
  mode: LibraryLoadMode;
  position: number;
  biddingMode?: BiddingMode;
  /** For shared games, use the shared game id */
  sharedGameId?: number;
}
